import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Baby, VaccinationRecord, VaccinationStatus } from '../types'
import { addDays, isPast, isToday, format } from 'date-fns'
import { useVaccineStore } from './useVaccineStore'
import { useBabyStore } from './useBabyStore'
import { useAuthStore } from './useAuthStore'
import { useVaccineDefinitions } from './useVaccineDefinitions'
import { useNotificationStore } from './useNotificationStore'
import { rescheduleAllNotifications } from '../services/notificationService'
import { getVaccineById, getDoseById } from '../services/data'
import { getEffectiveDate } from '../utils/vaccineDate'
import {
  getScheduleDependency,
  getAgeCapDate,
  resolvePlannedStatus,
  calculateBeyfortusDate,
  resolveCandidateDate,
  applyMinIntervalFloors,
} from '../utils/vaccineRules'

import { getExclusiveSkipRules, matchesSkipRule, getActiveSkipSources } from '../utils/vaccineCompletionRules'
import { getScheduleDependentRecords, getRecalculatedPlannedDates } from '../utils/vaccineDependencies'

export const useAppStore = defineStore('app', () => {
  const vaccineStore = useVaccineStore()
  const babyStore = useBabyStore()
  const vaccineDefinitions = useVaccineDefinitions()

  /* =========================
     State
  ========================= */
  const babies = ref<Baby[]>([])
  const currentBabyId = ref<string | null>(null)
  const hasOnboarded = ref(false)

  /* =========================
     Computed
  ========================= */
  const currentBaby = computed(() =>
    babies.value.find(b => b.id === currentBabyId.value) ?? null
  )

  const currentBabyRecords = computed(() =>
    vaccineStore.records
      .filter(r => r.babyId === currentBabyId.value)
      .sort((a, b) => {
        return getEffectiveDate(a).localeCompare(getEffectiveDate(b))
      })
  )

  const allBabiesRecords = computed(() =>
    vaccineStore.records
      .sort((a, b) => {
        return getEffectiveDate(a).localeCompare(getEffectiveDate(b))
      })
  )

  /* =========================
     Init
  ========================= */
  const initFromCloud = async () => {
    // 先確保家庭 profile 已載入（解決 init() 背景載入與 initFromCloud 的 race condition）
    // 也確保 pull-to-refresh 時能即時反映成員狀態變更（如 owner 撤銷存取）
    const auth = useAuthStore()
    if (auth.uid) {
      await auth.loadUserProfile()
    }

    await babyStore.loadBabies()
    await vaccineStore.loadRecords()

    babies.value = [...babyStore.babies]

    // 只在沒有當前寶寶或當前寶寶已不存在時，才設置為第一個寶寶
    if (!currentBabyId.value || !babies.value.find(b => b.id === currentBabyId.value)) {
      currentBabyId.value = babies.value[0]?.id ?? null
    }

    // 如果已有寶寶資料，標記為已完成 onboarding
    if (babies.value.length > 0) {
      hasOnboarded.value = true
    }

    // 補建缺少的疫苗紀錄（新增疫苗定義後，舊寶寶需要補上）
    if (babies.value.length > 0) {
      await createMissingRecordsForExistingBabies()
    }

    // 檢查是否有紀錄已超過接種年齡上限（例如輪狀病毒過齡），轉為 expired
    if (babies.value.length > 0) {
      await syncExpiredStatuses()
    }
  }

  /**
   * 掃描所有 planned/overdue 記錄，若已超過該劑次查核過的接種年齡上限則轉為 expired。
   * 與 recalculateSubsequentDoses 等處的即時判斷互補：這裡負責「使用者長時間沒有觸發任何確認接種操作，
   * 但年齡已經超過上限」的既有紀錄，確保每次開啟 App 時都會被檢查到，而不是只在互動當下才更新。
   */
  const syncExpiredStatuses = async () => {
    for (const baby of babies.value) {
      const babyRecords = vaccineStore.records.filter(r => r.babyId === baby.id)
      const birth = new Date(baby.birthDate)
      const candidates = babyRecords.filter(r => r.status === 'planned' || r.status === 'overdue')

      for (const record of candidates) {
        const dose = vaccineDefinitions.getDoseById(record.doseId)
        if (!dose) continue

        const ageCapDate = getAgeCapDate(dose, birth, babyRecords)
        if (ageCapDate && isPast(ageCapDate) && !isToday(ageCapDate)) {
          await vaccineStore.updateRecord(record.id, { status: 'expired' })
        }
      }
    }
  }

  /* =========================
     Baby actions
  ========================= */
  const addBaby = async (baby: Baby) => {
    console.log('[useAppStore] addBaby 開始:', baby.id)

    try {
      console.log('[useAppStore] 步驟 1: 呼叫 babyStore.setBaby...')
      await babyStore.setBaby(baby)
      console.log('[useAppStore] 步驟 1 完成: setBaby 成功')

      babies.value.push(baby)
      currentBabyId.value = baby.id

      // 標記已完成 onboarding
      hasOnboarded.value = true

      console.log('[useAppStore] 步驟 2: 呼叫 generateVaccinationRecords...')
      await generateVaccinationRecords(baby.id, baby.birthDate, baby)
      console.log('[useAppStore] 步驟 2 完成: generateVaccinationRecords 成功')

      // 自動重新排程推播通知（包含新寶寶的疫苗）
      console.log('[useAppStore] 步驟 3: 呼叫 autoRescheduleNotifications...')
      await autoRescheduleNotifications(baby.id)
      console.log('[useAppStore] 步驟 3 完成: autoRescheduleNotifications 成功')

      console.log('[useAppStore] addBaby 全部完成')
    } catch (error: any) {
      console.error('[useAppStore] 新增寶寶失敗:', error)
      console.error('[useAppStore] 錯誤詳情:', {
        message: error?.message,
        code: error?.code,
        name: error?.name,
        stack: error?.stack
      })
      throw error
    }
  }

  const switchBaby = (babyId: string) => {
    if (babies.value.find(b => b.id === babyId)) {
      currentBabyId.value = babyId
    }
  }

  const deleteBaby = async (babyId: string) => {
    try {
      // 刪除寶寶的所有疫苗記錄
      await vaccineStore.deleteRecordsByBabyId(babyId)

      // 刪除寶寶資料
      await babyStore.deleteBaby(babyId)

      // 更新本地狀態
      babies.value = babies.value.filter(b => b.id !== babyId)

      // 如果刪除的是當前寶寶，切換到第一個寶寶
      if (currentBabyId.value === babyId) {
        currentBabyId.value = babies.value[0]?.id ?? null
      }

      // 如果沒有寶寶了，重置 onboarding 狀態
      if (babies.value.length === 0) {
        hasOnboarded.value = false
      }
    } catch (error) {
      console.error('刪除寶寶失敗:', error)
      throw error
    }
  }

  /* =========================
     Vaccine generation
  ========================= */
  const generateVaccinationRecords = async (
    babyId: string,
    birthDate: string,
    baby?: Baby
  ) => {
    const birth = new Date(birthDate)
    const promises: Promise<void>[] = []
    const currentVersion = vaccineDefinitions.currentVersion

    // 使用疫苗定義 store 中的 doses
    const doses = vaccineDefinitions.getAllDoses()

    // 建立 (vaccineId-doseIndex) 到 plannedDate 的映射（用於計算依賴劑次）
    const doseDateMap = new Map<string, Date>()
    // 建立 doseId 到有效日期的映射（用於 minIntervals floor，doseId 才能明確指向 PCV13 這類 doseIndex 非時序排列的劑次）
    const doseDateMapById = new Map<string, Date>()
    const maternalRSVVaccinated = baby?.maternalRSV?.vaccinated ?? false

    for (const dose of doses) {
      const candidateDate = resolveCandidateDate(dose, {
        birthDate: birth,
        doseDates: doseDateMap,
        allDoses: doses,
        maternalRSVVaccinated,
      })

      if (!candidateDate) continue

      const plannedDate = applyMinIntervalFloors(dose, candidateDate, birth, doseDateMapById)

      // 記錄此劑次的計劃日期（用於後續劑次計算）
      const doseMapKey = `${dose.vaccineId}-${dose.doseIndex}`
      doseDateMap.set(doseMapKey, plannedDate)
      doseDateMapById.set(dose.id, plannedDate)

      const status: VaccinationStatus = resolvePlannedStatus(
        plannedDate,
        getAgeCapDate(dose, birth, [])
      )

      const record: VaccinationRecord = {
        id: `${babyId}_${dose.id}_${Date.now()}`,
        babyId,
        vaccineId: dose.vaccineId,
        doseId: dose.id,
        doseIndex: dose.doseIndex,
        plannedDate: format(plannedDate, 'yyyy-MM-dd'),
        status,
        calendarSynced: false,
        dataVersion: currentVersion, // 記錄使用的疫苗資料版本
        createdAt: new Date().toISOString(),
      }

      promises.push(vaccineStore.setRecord(record))
    }



    await Promise.all(promises)
  }

  /* =========================
     Calendar
  ========================= */
  const markCalendarSynced = async (recordId: string) => {
    await vaccineStore.updateRecord(recordId, {
      calendarSynced: true,
    })
  }

  const toggleCalendarSync = async (recordId: string) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record) return

    await vaccineStore.updateRecord(recordId, {
      calendarSynced: !record.calendarSynced,
    })
  }

  /* =========================
     Confirm vaccination
  ========================= */
  const confirmVaccination = async (
    recordId: string,
    actualDate: string,
    options?: {
      rotaType?: '2' | '3'
      fluFirstTime?: boolean
    }
  ) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record) return

    await vaccineStore.updateRecord(record.id, {
      status: 'completed',
      actualDate,
      calendarSynced: false,
    })

    // 輪狀病毒特殊處理
    if (record.vaccineId === 'rota' && record.doseIndex === 1) {
      await handleRotaVaccineConfirmation(record, actualDate, options?.rotaType)
    }
    // 卡介苗特殊處理
    else if (record.vaccineId === 'bcg' && record.doseIndex === 1) {
      await handleBCGVaccineConfirmation(record)
    }
    // 流感疫苗特殊處理
    else if (record.vaccineId === 'flu') {
      await handleFluVaccineConfirmation(record, actualDate)
    }
    // 一般疫苗
    else {
      await recalculateSubsequentDoses(
        record.babyId,
        record.vaccineId,
        record.doseIndex,
        actualDate
      )
    }

    // PCV13/PCV20 互斥：確認一方自動 skip 另一方
    await handleExclusiveDoseSkip(record)

    // 自動重新排程推播通知
    await autoRescheduleNotifications(record.babyId)
  }

  /**
   * 取消接種確認（改回未接種），還原 confirmVaccination 造成的連鎖影響：
   * - 自動被 skip 的劑次（若還有其他理由成立則維持 skipped）
   * - 後續劑次被重新計算過的預計日期
   * 不會處理已加入行事曆的事件，使用者需自行調整。
   */
  const unconfirmVaccination = async (recordId: string) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record || record.status !== 'completed') return

    const effectiveDateStr = record.customPlannedDate ?? record.plannedDate
    const d = new Date(effectiveDateStr)

    const unconfirmDose = vaccineDefinitions.getDoseById(record.doseId)
    const unconfirmBaby = babies.value.find(item => item.id === record.babyId)
    const unconfirmSiblings = vaccineStore.records.filter(item => item.babyId === record.babyId)
    const unconfirmAgeCapDate = unconfirmDose && unconfirmBaby
      ? getAgeCapDate(unconfirmDose, new Date(unconfirmBaby.birthDate), unconfirmSiblings)
      : null

    await vaccineStore.updateRecord(record.id, {
      actualDate: null,
      status: resolvePlannedStatus(d, unconfirmAgeCapDate),
      calendarSynced: false,
    })

    // 輪狀病毒特殊處理
    if (record.vaccineId === 'rota' && record.doseIndex === 1) {
      await unconfirmRotaVaccine(record, effectiveDateStr)
    }
    // 卡介苗特殊處理
    else if (record.vaccineId === 'bcg' && record.doseIndex === 1) {
      await unconfirmBCGVaccine(record)
    }
    // 流感疫苗特殊處理
    else if (record.vaccineId === 'flu') {
      await unconfirmFluVaccine(record, effectiveDateStr)
    }
    // 一般疫苗
    else {
      await recalculateSubsequentDoses(
        record.babyId,
        record.vaccineId,
        record.doseIndex,
        effectiveDateStr
      )
    }

    // 撤銷因此劑而被互斥規則 skip 的其他劑次
    await unconfirmExclusiveDoseSkip(record)

    // 自動重新排程推播通知
    await autoRescheduleNotifications(record.babyId)
  }

  /**
   * 使用者手動將一劑標記為不用接種。
   * 保留既有 skipped 狀態，並以 manualSkip 與系統規則造成的跳過區分。
   */
  const markVaccinationAsSkipped = async (recordId: string) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record || record.status === 'completed' || record.status === 'skipped') return

    await vaccineStore.updateRecord(record.id, {
      status: 'skipped',
      manualSkip: true,
      calendarSynced: false,
    })

    // 既有排程器只會安排 planned 紀錄；重排後此劑的本機提醒會被移除。
    await autoRescheduleNotifications(record.babyId)
  }

  /**
   * 復原使用者手動標記的不用接種劑次。
   * 僅允許復原 manualSkip，避免誤覆寫系統規則造成的 skipped 狀態。
   */
  const restoreManuallySkippedVaccination = async (recordId: string) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record || record.status !== 'skipped' || !record.manualSkip) {
      return { status: 'not-restored' as const }
    }

    const sourceDoseIds = getActiveSkipSources(record, vaccineStore.records, vaccineDefinitions.getDoseById)
    await refreshRestoredSchedule(record, sourceDoseIds)

    await autoRescheduleNotifications(record.babyId)
    return sourceDoseIds.length
      ? { status: 'still-skipped' as const, sourceDoseIds }
      : { status: 'restored' as const }
  }

  /** 手動復原與撤銷自動略過共用日期重算，保留其他劑次的手動狀態與自訂日期。 */
  const refreshRestoredSchedule = async (record: VaccinationRecord, sourceDoseIds: string[]) => {
    const baby = babies.value.find(item => item.id === record.babyId)
    const babyRecords = vaccineStore.records.filter(item => item.babyId === record.babyId)
    const doses = vaccineDefinitions.getAllDoses()
    const dependents = getScheduleDependentRecords(record, babyRecords, doses)
    const dates = baby
      ? getRecalculatedPlannedDates([record, ...dependents], babyRecords, doses, baby)
      : new Map<string, string>()

    // 撤銷手動決定後，仍成立的自動略過繼續保留。
    // 同時更新隱藏中的下游日期，避免經由隱藏中間劑次傳遞舊日期。
    // 先完成下游更新才解除隱藏；寫入失敗時使用者仍能重試復原。
    for (const target of [...dependents, record]) {
      const plannedDate = dates.get(target.doseId) ?? target.plannedDate
      const dose = vaccineDefinitions.getDoseById(target.doseId)
      const ageCapDate = dose && baby ? getAgeCapDate(dose, new Date(baby.birthDate), babyRecords) : null
      const restoredStatus = resolvePlannedStatus(new Date(target.customPlannedDate ?? plannedDate), ageCapDate)
      const isRestoring = target.id === record.id
      // 只有實際日期改變，或復原手動略過（提示過使用者自行刪除行事曆事件）時才清除同步標記，
      // 避免日期未變的下游劑次失去 ✓ 而被重複加入行事曆。
      const effectiveDateChanged = (target.customPlannedDate ?? plannedDate) !== (target.customPlannedDate ?? target.plannedDate)
      const clearCalendarSync = effectiveDateChanged || (isRestoring && target.manualSkip === true)
      await vaccineStore.updateRecord(target.id, {
        plannedDate,
        status: isRestoring
          ? (sourceDoseIds.length ? 'skipped' : restoredStatus)
          : (target.status === 'skipped' ? 'skipped' : restoredStatus),
        ...(clearCalendarSync ? { calendarSynced: false } : {}),
        ...(isRestoring ? { manualSkip: false, skippedByDoseIds: sourceDoseIds.length ? sourceDoseIds : null } : {}),
      })
    }
  }

  /**
   * 將某筆紀錄標為自動 skip，並記錄來源劑次 doseId（provenance）
   * 供 unconfirmVaccination 判斷改回未接種時是否還能精確還原
   */
  const applyAutoSkip = async (targetId: string, sourceDoseId: string) => {
    const target = vaccineStore.records.find(r => r.id === targetId)
    if (!target) return

    const sources = target.skippedByDoseIds ?? []
    await vaccineStore.updateRecord(targetId, {
      status: 'skipped',
      skippedByDoseIds: sources.includes(sourceDoseId) ? sources : [...sources, sourceDoseId],
    })
  }

  /**
   * 撤銷單一來源造成的自動 skip。
   * 若該筆紀錄還有其他來源仍然成立，維持 skipped；否則還原成 planned/overdue。
   */
  const revertAutoSkip = async (targetId: string, sourceDoseId: string) => {
    const target = vaccineStore.records.find(r => r.id === targetId)
    if (!target || target.status !== 'skipped') return

    // 來源不明的舊略過，無法判定是否由本次取消的紀錄造成；不能擅自解除。
    if (!target.manualSkip && !target.skippedByDoseIds?.length) return

    // 舊資料可能漏記有效來源或殘留已取消的來源，必須依目前完成紀錄重查。
    const activeSources = getActiveSkipSources(target, vaccineStore.records, vaccineDefinitions.getDoseById)
      .filter(id => id !== sourceDoseId)
    if (target.manualSkip || activeSources.length) {
      await vaccineStore.updateRecord(targetId, { skippedByDoseIds: activeSources.length ? activeSources : null })
      return
    }

    await refreshRestoredSchedule(target, activeSources)
  }

  /**
   * 互斥劑次邏輯：確認接種其中一方時，自動將規則指定的其他劑次標為 skipped。
   * 已有自動 skip provenance 的目標仍會累積來源，確保撤銷其中一筆時不會過早復原。
   */
  const handleExclusiveDoseSkip = async (completedRecord: VaccinationRecord) => {
    const rules = getExclusiveSkipRules(completedRecord.doseId, vaccineDefinitions.getDoseById(completedRecord.doseId))
    for (const rule of rules) {
      const targets = vaccineStore.records.filter(target =>
        target.babyId === completedRecord.babyId && matchesSkipRule(target, rule))
      for (const target of targets) await applyAutoSkip(target.id, completedRecord.doseId)
    }
  }

  /**
   * 撤銷互斥規則造成的 skip（供 unconfirmVaccination 使用）
   */
  const unconfirmExclusiveDoseSkip = async (record: VaccinationRecord) => {
    const rules = getExclusiveSkipRules(record.doseId, vaccineDefinitions.getDoseById(record.doseId))
    for (const rule of rules) {
      const targets = vaccineStore.records.filter(target =>
        target.babyId === record.babyId && target.status === 'skipped' && rule.targetDoseIds.includes(target.doseId))
      for (const target of targets) await revertAutoSkip(target.id, record.doseId)
    }
  }

  /**
   * 自動重新排程推播通知（疫苗完成後）
   */
  const autoRescheduleNotifications = async (_babyId: string) => {
    const notificationStore = useNotificationStore()

    // 只有在有權限且有設定提醒時間點時才重新排程
    if (!notificationStore.hasPermission || notificationStore.settings.timings.length === 0) {
      return
    }

    try {
      // 使用所有寶寶的疫苗記錄（不只是當前寶寶）
      // 這樣才能正確找出所有寶寶中最近的疫苗日期
      const allRecords = vaccineStore.records

      // 重新排程通知
      await rescheduleAllNotifications(
        allRecords,
        (bid) => {
          const baby = babies.value.find(b => b.id === bid)
          return baby?.nickname || '寶寶'
        },
        (vaccineId) => {
          const vaccine = getVaccineById(vaccineId)
          return vaccine?.name || ''
        },
        (doseId) => {
          const dose = getDoseById(doseId)
          return dose?.label || ''
        },
        notificationStore.settings.timings
      )

    } catch (error) {
      console.error('自動重新排程失敗:', error)
    }
  }

  /**
   * 處理輪狀病毒疫苗確認接種的特殊邏輯
   * 邏輯：
   * - 確認第一劑時，如果選擇 2 劑型，則將第 3 劑標記為 skipped
   * - 然後重新計算後續劑次（第 2 劑和第 3 劑）
   */
  const handleRotaVaccineConfirmation = async (
    record: VaccinationRecord,
    actualDate: string,
    rotaType?: '2' | '3'
  ) => {
    // 如果選擇 2 劑型，將第 3 劑標記為 skipped
    if (rotaType === '2') {
      const dose3Record = vaccineStore.records.find(
        r =>
          r.babyId === record.babyId &&
          r.vaccineId === 'rota' &&
          r.doseIndex === 3 &&
          r.status !== 'completed'
      )

      if (dose3Record) {
        await applyAutoSkip(dose3Record.id, record.doseId)
      }
    }

    // 重新計算後續劑次（第 2 劑和第 3 劑的日期）
    await recalculateSubsequentDoses(
      record.babyId,
      record.vaccineId,
      record.doseIndex,
      actualDate
    )
  }

  /**
   * 撤銷輪狀病毒第 1 劑確認：
   * - 撤銷因此劑（2 劑型）而被 skip 的第 3 劑
   * - 重新計算後續劑次（第 2 劑、第 3 劑）的預計日期
   */
  const unconfirmRotaVaccine = async (record: VaccinationRecord, baseDateStr: string) => {
    const dose3Record = vaccineStore.records.find(
      r => r.babyId === record.babyId && r.vaccineId === 'rota' && r.doseIndex === 3
    )
    if (dose3Record) {
      await revertAutoSkip(dose3Record.id, record.doseId)
    }

    await recalculateSubsequentDoses(record.babyId, 'rota', 1, baseDateStr)
  }

  /**
   * 處理卡介苗確認接種的特殊邏輯
   * 邏輯：
   * - 確認第一劑（單劑）後，將第 2 劑（補種）標記為 skipped
   */
  const handleBCGVaccineConfirmation = async (
    record: VaccinationRecord
  ) => {
    // 找到補種劑次（doseIndex = 2）
    const boosterRecord = vaccineStore.records.find(
      r =>
        r.babyId === record.babyId &&
        r.vaccineId === 'bcg' &&
        r.doseIndex === 2 &&
        r.status !== 'completed'
    )

    if (boosterRecord) {
      await applyAutoSkip(boosterRecord.id, record.doseId)
    }
  }

  /**
   * 撤銷卡介苗第 1 劑確認：撤銷因此劑而被 skip 的補種劑次（doseIndex 2）
   */
  const unconfirmBCGVaccine = async (record: VaccinationRecord) => {
    const boosterRecord = vaccineStore.records.find(
      r => r.babyId === record.babyId && r.vaccineId === 'bcg' && r.doseIndex === 2
    )
    if (boosterRecord) {
      await revertAutoSkip(boosterRecord.id, record.doseId)
    }
  }

  /**
   * 處理流感疫苗確認接種的特殊邏輯
   * 邏輯：
   * - 確認第一劑後，同年的第二劑保留並更新日期（除非已被 skipped）
   * - 其他年份的第二劑都標記為 skipped
   * - 一旦某年的第二劑被 skipped，即使之後確認該年第一劑也不會恢復
   */
  const handleFluVaccineConfirmation = async (
    record: VaccinationRecord,
    actualDate: string
  ) => {
    const base = new Date(actualDate)

    // 取得此寶寶的所有流感疫苗紀錄
    const fluRecords = vaccineStore.records.filter(
      r => r.babyId === record.babyId && r.vaccineId === 'flu'
    )

    // 判斷是否為第一劑（奇數 doseIndex）
    const isFirstDose = record.doseIndex % 2 === 1

    if (isFirstDose) {
      // 找到同年的第二劑（doseIndex + 1），且狀態為 planned（不包含 skipped、overdue、completed）
      const sameYearSecondDose = fluRecords.find(
        r => r.doseIndex === record.doseIndex + 1 &&
             r.status === 'planned'  // 只有 planned 狀態才更新
      )

      // 找到其他年份的第二劑（偶數 doseIndex，但不是同年的）
      // 已隱藏的劑次也要記錄自動略過來源，以便日後復原／撤銷。
      const otherYearSecondDoses = fluRecords.filter(
        r =>
          r.doseIndex % 2 === 0 && // 第二劑（偶數）
          r.doseIndex !== record.doseIndex + 1 && // 不是同年的第二劑
          r.status !== 'completed'
      )

      // 1. 更新同年的第二劑日期（第一劑後 4 週）
      // 注意：只有 planned 狀態才更新，skipped/overdue/completed 都不處理
      if (sameYearSecondDose) {
        const newDate = addDays(base, 28)
        await vaccineStore.updateRecord(sameYearSecondDose.id, {
          plannedDate: format(newDate, 'yyyy-MM-dd'),
          calendarSynced: false,
        })
      }

      // 2. 將其他年份的第二劑標記為 skipped
      for (const otherSecondDose of otherYearSecondDoses) {
        await applyAutoSkip(otherSecondDose.id, record.doseId)
      }
    } else {
      // 如果確認的是第二劑，不做特殊處理
      // 只更新後續劑次（如果有的話）
      await recalculateSubsequentDoses(
        record.babyId,
        record.vaccineId,
        record.doseIndex,
        actualDate
      )
    }
  }

  /**
   * 撤銷流感疫苗確認接種：
   * - 第二劑：僅重新計算後續劑次
   * - 第一劑：撤銷因此劑而被 skip 的其他年份第二劑，
   *   並重新計算後續劑次（同年度第二劑的 intervalFromDose 規則會以 baseDateStr 為基準重算，
   *   自動取代先前 handleFluVaccineConfirmation 寫入的 +28 天覆寫值）
   *   （unskip 優先使用 provenance；舊資料缺少 provenance 時，以目前所有紀錄的完成狀態重新判斷 skip 理由是否仍成立）
   */
  const unconfirmFluVaccine = async (record: VaccinationRecord, baseDateStr: string) => {
    const isFirstDose = record.doseIndex % 2 === 1

    if (!isFirstDose) {
      await recalculateSubsequentDoses(record.babyId, 'flu', record.doseIndex, baseDateStr)
      return
    }

    const fluRecords = vaccineStore.records.filter(
      r => r.babyId === record.babyId && r.vaccineId === 'flu'
    )

    // 撤銷其他年份第二劑的自動 skip
    const otherYearSecondDoses = fluRecords.filter(
      r => r.doseIndex % 2 === 0 && r.doseIndex !== record.doseIndex + 1 && r.status === 'skipped'
    )

    for (const target of otherYearSecondDoses) {
      if (target.skippedByDoseIds && target.skippedByDoseIds.length > 0) {
        await revertAutoSkip(target.id, record.doseId)
        continue
      }

      // 舊資料沒有 provenance：用目前事實重新判斷是否還有其他已完成第一劑構成 skip 理由
      const stillJustified = fluRecords.some(
        other =>
          other.id !== record.id &&
          other.doseIndex % 2 === 1 &&
          other.status === 'completed' &&
          other.doseIndex + 1 !== target.doseIndex
      )
      if (!stillJustified && !target.manualSkip) {
        const effectiveDateStr = target.customPlannedDate ?? target.plannedDate
        const targetDose = vaccineDefinitions.getDoseById(target.doseId)
        const baby = babies.value.find(item => item.id === target.babyId)
        const ageCapDate = targetDose && baby
          ? getAgeCapDate(targetDose, new Date(baby.birthDate), fluRecords)
          : null
        await vaccineStore.updateRecord(target.id, {
          status: resolvePlannedStatus(new Date(effectiveDateStr), ageCapDate),
        })
      }
    }

    // 重新計算後續劑次（同年度第二劑會依 intervalFromDose 規則以 baseDateStr 為基準重算，取代先前的 +28 天覆寫值）
    await recalculateSubsequentDoses(record.babyId, 'flu', record.doseIndex, baseDateStr)
  }

  const recalculateSubsequentDoses = async (
    babyId: string,
    vaccineId: string,
    baseDoseIndex: number,
    baseDate: string
  ) => {
    const base = new Date(baseDate)

    // 使用疫苗定義 store 中的 doses
    const doses = vaccineDefinitions.getAllDoses()
    const baby = babies.value.find(item => item.id === babyId)
    if (!baby) return

    const birth = new Date(baby.birthDate)
    const maternalRSVVaccinated = baby.maternalRSV?.vaccinated ?? false

    const subsequent = vaccineStore.records
      .filter(
        r =>
          r.babyId === babyId &&
          r.vaccineId === vaccineId &&
          r.doseIndex > baseDoseIndex &&
          r.status !== 'completed' &&
          r.status !== 'skipped'
      )
      .sort((a, b) => a.doseIndex - b.doseIndex)

    const babyRecords = vaccineStore.records.filter(item => item.babyId === babyId)
    const doseDates = new Map<string, Date>()
    // doseId 到有效日期的映射，供 minIntervals floor 使用（doseId 才能明確指向前一劑，doseIndex 對 PCV13 這類劑次不可靠）
    const doseDatesById = new Map<string, Date>()
    for (const record of babyRecords) {
      const eff = new Date(getEffectiveDate(record))
      doseDates.set(`${record.vaccineId}-${record.doseIndex}`, eff)
      doseDatesById.set(record.doseId, eff)
    }
    doseDates.set(`${vaccineId}-${baseDoseIndex}`, base)
    const baseDoseId = doses.find(d => d.vaccineId === vaccineId && d.doseIndex === baseDoseIndex)?.id
    if (baseDoseId) doseDatesById.set(baseDoseId, base)

    for (const record of subsequent) {
      const def = doses.find(
        d =>
          d.vaccineId === record.vaccineId &&
          d.doseIndex === record.doseIndex
      )

      if (!def) {
        continue
      }

      const candidateDate = resolveCandidateDate(def, {
        birthDate: birth,
        doseDates,
        allDoses: doses,
        maternalRSVVaccinated,
      })

      if (!candidateDate) {
        continue
      }

      const newDate = applyMinIntervalFloors(def, candidateDate, birth, doseDatesById)
      const ageCapDate = getAgeCapDate(def, birth, babyRecords)

      await vaccineStore.updateRecord(record.id, {
        plannedDate: format(newDate, 'yyyy-MM-dd'),
        status: resolvePlannedStatus(newDate, ageCapDate),
        calendarSynced: false,
      })

      doseDates.set(`${record.vaccineId}-${record.doseIndex}`, newDate)
      doseDatesById.set(record.doseId, newDate)
    }
  }

  /* =========================
     Vaccine Data Update
  ========================= */

  /**
   * 為既有寶寶補建缺少的疫苗紀錄
   * 當 app 新增疫苗定義後，舊寶寶不會自動產生對應紀錄，這裡補上
   */
  const createMissingRecordsForExistingBabies = async () => {
    const doses = vaccineDefinitions.getAllDoses()
    const currentVersion = vaccineDefinitions.currentVersion

    for (const baby of babies.value) {
      const babyRecords = vaccineStore.records.filter(r => r.babyId === baby.id)
      const existingDoseIds = new Set(babyRecords.map(r => r.doseId))

      const birth = new Date(baby.birthDate)
      const doseDateMap = new Map<string, Date>()
      const doseDateMapById = new Map<string, Date>()
      const maternalRSVVaccinated = baby.maternalRSV?.vaccinated ?? false

      // 先把已有紀錄的日期填入 map
      for (const record of babyRecords) {
        const eff = new Date(getEffectiveDate(record))
        doseDateMap.set(`${record.vaccineId}-${record.doseIndex}`, eff)
        doseDateMapById.set(record.doseId, eff)
      }

      for (const dose of doses) {
        if (existingDoseIds.has(dose.id)) continue

        const candidateDate = resolveCandidateDate(dose, {
          birthDate: birth,
          doseDates: doseDateMap,
          allDoses: doses,
          maternalRSVVaccinated,
        })

        if (!candidateDate) continue

        const plannedDate = applyMinIntervalFloors(dose, candidateDate, birth, doseDateMapById)

        const doseMapKey = `${dose.vaccineId}-${dose.doseIndex}`
        doseDateMap.set(doseMapKey, plannedDate)
        doseDateMapById.set(dose.id, plannedDate)

        const status: VaccinationStatus = resolvePlannedStatus(
          plannedDate,
          getAgeCapDate(dose, birth, babyRecords)
        )

        const record: VaccinationRecord = {
          id: `${baby.id}_${dose.id}_${Date.now()}`,
          babyId: baby.id,
          vaccineId: dose.vaccineId,
          doseId: dose.id,
          doseIndex: dose.doseIndex,
          plannedDate: format(plannedDate, 'yyyy-MM-dd'),
          status,
          calendarSynced: false,
          dataVersion: currentVersion,
          createdAt: new Date().toISOString(),
        }

        await vaccineStore.setRecord(record)
      }
    }
  }

  /**
   * 自動更新所有寶寶的疫苗記錄到新版本
   * 只更新 planned/overdue 狀態的記錄，保留 completed/skipped
   * 返回更新的記錄數量
   */
  const updateVaccineRecordsToNewVersion = async (): Promise<number> => {
    if (babies.value.length === 0) {
      return 0
    }

    let updatedCount = 0
    const currentVersion = vaccineDefinitions.currentVersion

    // 先為既有寶寶補建缺少的疫苗紀錄（例如新增疫苗定義後）
    await createMissingRecordsForExistingBabies()

    for (const baby of babies.value) {
      // 取得此寶寶的所有記錄
      const babyRecords = vaccineStore.records.filter(r => r.babyId === baby.id)

      // 只處理 planned/overdue 狀態的記錄
      const recordsToUpdate = babyRecords.filter(
        r => r.status === 'planned' || r.status === 'overdue'
      )

      if (recordsToUpdate.length === 0) {
        continue
      }

      // 重新生成此寶寶的疫苗時間表
      const birth = new Date(baby.birthDate)
      const doses = vaccineDefinitions.getAllDoses()

      // 建立 (vaccineId-doseIndex) 到 plannedDate 的映射
      const doseDateMap = new Map<string, Date>()
      const doseDateMapById = new Map<string, Date>()
      const maternalRSVVaccinated = baby.maternalRSV?.vaccinated ?? false

      // 先處理已完成的疫苗及有自訂日期的疫苗，加入 map（用於計算後續劑次）
      for (const record of babyRecords) {
        if (record.status === 'completed' || record.customPlannedDate) {
          const eff = new Date(getEffectiveDate(record))
          doseDateMap.set(`${record.vaccineId}-${record.doseIndex}`, eff)
          doseDateMapById.set(record.doseId, eff)
        }
      }

      // 重新計算每個劑次的日期
      for (const dose of doses) {
        // 檢查此劑次是否需要更新
        const existingRecord = recordsToUpdate.find(
          r => r.vaccineId === dose.vaccineId && r.doseIndex === dose.doseIndex
        )

        if (!existingRecord) {
          continue
        }

        const candidateDate = resolveCandidateDate(dose, {
          birthDate: birth,
          doseDates: doseDateMap,
          allDoses: doses,
          maternalRSVVaccinated,
        })

        if (!candidateDate) continue

        const plannedDate = applyMinIntervalFloors(dose, candidateDate, birth, doseDateMapById)

        // 記錄此劑次的計劃日期（用於後續劑次計算）
        const doseMapKey = `${dose.vaccineId}-${dose.doseIndex}`
        doseDateMap.set(doseMapKey, plannedDate)
        doseDateMapById.set(dose.id, plannedDate)

        // 檢查日期是否有變化
        const newPlannedDateStr = format(plannedDate, 'yyyy-MM-dd')
        // 狀態用 effectiveDate 判斷（customPlannedDate 優先）
        const effectiveDate = existingRecord.customPlannedDate
          ? new Date(existingRecord.customPlannedDate)
          : plannedDate

        const ageCapDate = getAgeCapDate(dose, birth, babyRecords)

        if (newPlannedDateStr !== existingRecord.plannedDate) {
          const newStatus: VaccinationStatus = resolvePlannedStatus(effectiveDate, ageCapDate)

          await vaccineStore.updateRecord(existingRecord.id, {
            plannedDate: newPlannedDateStr,
            status: newStatus,
            dataVersion: currentVersion,
            calendarSynced: existingRecord.customPlannedDate ? existingRecord.calendarSynced : false,
          })

          updatedCount++
        } else {
          // 日期沒變，只更新版本號（同時用 effectiveDate 校正 status）
          const newStatus: VaccinationStatus = resolvePlannedStatus(effectiveDate, ageCapDate)

          await vaccineStore.updateRecord(existingRecord.id, {
            dataVersion: currentVersion,
            status: newStatus,
          })
        }
      }
    }

    return updatedCount
  }

  /* =========================
     Reset - 含容錯處理
  ========================= */
  const resetAllData = async () => {
    // 1. 嘗試刪除所有寶寶資料
    try {
      await babyStore.deleteAllBabies()
    } catch {
      // 不拋出錯誤，繼續執行
    }

    // 2. 嘗試刪除所有疫苗記錄
    try {
      await vaccineStore.deleteAllRecords()
    } catch {
      // 不拋出錯誤，繼續執行
    }

    // 3. 清理本地狀態（無論 Firestore 是否成功）
    babies.value = []
    currentBabyId.value = null
    hasOnboarded.value = false
  }

  /* =========================
     RSV / Beyfortus
  ========================= */

  /**
   * 更新媽媽孕期 RSV 疫苗狀態，並重新計算寶寶 Beyfortus plannedDate
   */
  const updateMaternalRSV = async (
    babyId: string,
    data: { vaccinated: boolean; date?: string; brand?: string }
  ) => {
    await babyStore.updateBaby(babyId, { maternalRSV: data })

    const baby = babies.value.find(b => b.id === babyId)
    if (baby) baby.maternalRSV = data

    const rsvRecord = vaccineStore.records.find(
      r => r.babyId === babyId && r.vaccineId === 'rsv' && r.doseIndex === 1
    )
    // 寶寶本身的 RSV 紀錄若已完成接種，不應被媽媽疫苗狀態變更覆寫回未完成
    if (rsvRecord && baby && rsvRecord.status !== 'completed') {
      const newDate = calculateBeyfortusDate(baby.birthDate, data.vaccinated)
      const newDateStr = format(newDate, 'yyyy-MM-dd')
      await vaccineStore.updateRecord(rsvRecord.id, {
        plannedDate: newDateStr,
        status: rsvRecord.status === 'skipped' ? 'skipped' : resolvePlannedStatus(new Date(rsvRecord.customPlannedDate ?? newDateStr), null),
        calendarSynced: false,
      })
    }

    await autoRescheduleNotifications(babyId)
  }

  /**
   * 手動更新某筆記錄的 plannedDate（用於 flexibleTiming 疫苗）
   */
  const updatePlannedDate = async (recordId: string, newDate: string) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record) return

    // 狀態依有效日期（自訂日期優先）與接種年齡上限判斷，過齡時維持 expired
    const dose = vaccineDefinitions.getDoseById(record.doseId)
    const baby = babies.value.find(item => item.id === record.babyId)
    const babyRecords = vaccineStore.records.filter(item => item.babyId === record.babyId)
    const ageCapDate = dose && baby ? getAgeCapDate(dose, new Date(baby.birthDate), babyRecords) : null
    await vaccineStore.updateRecord(recordId, {
      plannedDate: newDate,
      status: record.status === 'skipped'
        ? 'skipped'
        : resolvePlannedStatus(new Date(record.customPlannedDate ?? newDate), ageCapDate),
    })

    await autoRescheduleNotifications(record.babyId)
  }

  /**
   * 使用者自訂預計施打日期
   */
  const updateCustomPlannedDate = async (recordId: string, newDate: string | null) => {
    const record = vaccineStore.records.find(r => r.id === recordId)
    if (!record) return

    const effectiveDateStr = newDate ?? record.plannedDate
    const d = new Date(effectiveDateStr)

    const dose = vaccineDefinitions.getDoseById(record.doseId)
    const baby = babies.value.find(item => item.id === record.babyId)
    const babyRecords = vaccineStore.records.filter(item => item.babyId === record.babyId)
    const ageCapDate = dose && baby ? getAgeCapDate(dose, new Date(baby.birthDate), babyRecords) : null

    const updates: Partial<VaccinationRecord> = {
      customPlannedDate: newDate,
      calendarSynced: false,
    }

    // 自訂日期不能解除使用者或系統已設定的略過狀態，否則會在沒有
    // 確認的情況下把該劑重新加入提醒排程。
    if (record.status !== 'skipped') {
      updates.status = resolvePlannedStatus(d, ageCapDate)
    }

    await vaccineStore.updateRecord(record.id, updates)

    // 如果後續劑次依賴此劑次（intervalFromDose），連動更新
    await recalculateSubsequentPlannedDates(record)

    await autoRescheduleNotifications(record.babyId)
  }

  /**
   * 當使用者修改 customPlannedDate 後，連動更新依賴此劑次的後續劑次 plannedDate
   * 只影響 status 為 planned/overdue 的紀錄
   */
  const recalculateSubsequentPlannedDates = async (changedRecord: VaccinationRecord) => {
    const doses = vaccineDefinitions.getAllDoses()

    // 找出依賴此劑次的後續劑次定義：官方時程依賴（scheduleRule/intervalFromDose）
    // 或 minIntervals floor 依賴（使用者手動調整日期時，也要能觸發下游 floor 重算）
    const dependentDoses = doses.filter(d => {
      const dependency = getScheduleDependency(d)
      const matchesSchedule =
        dependency?.vaccineId === changedRecord.vaccineId &&
        dependency.doseIndex === changedRecord.doseIndex
      const matchesMinInterval = d.minIntervals?.some(m => m.fromDoseId === changedRecord.doseId) ?? false
      return matchesSchedule || matchesMinInterval
    })

    if (dependentDoses.length === 0) return

    const baby = babies.value.find(item => item.id === changedRecord.babyId)
    if (!baby) return

    const birth = new Date(baby.birthDate)
    const maternalRSVVaccinated = baby.maternalRSV?.vaccinated ?? false
    const babyRecords = vaccineStore.records.filter(r => r.babyId === changedRecord.babyId)

    const doseDates = new Map<string, Date>()
    const doseDatesById = new Map<string, Date>()
    for (const record of babyRecords) {
      const eff = new Date(getEffectiveDate(record))
      doseDates.set(`${record.vaccineId}-${record.doseIndex}`, eff)
      doseDatesById.set(record.doseId, eff)
    }
    // 保險起見用被修改紀錄目前的 effectiveDate 覆寫一次（呼叫端可能剛更新但陣列引用尚未同步）
    const changedEff = new Date(getEffectiveDate(changedRecord))
    doseDates.set(`${changedRecord.vaccineId}-${changedRecord.doseIndex}`, changedEff)
    doseDatesById.set(changedRecord.doseId, changedEff)

    for (const depDose of dependentDoses) {
      const depRecord = vaccineStore.records.find(
        r => r.babyId === changedRecord.babyId &&
             r.vaccineId === depDose.vaccineId &&
             r.doseIndex === depDose.doseIndex &&
             r.status !== 'completed' &&
             r.status !== 'skipped'
      )

      if (!depRecord) continue

      const candidateDate = resolveCandidateDate(depDose, {
        birthDate: birth,
        doseDates,
        allDoses: doses,
        maternalRSVVaccinated,
      })

      if (!candidateDate) continue

      const newDate = applyMinIntervalFloors(depDose, candidateDate, birth, doseDatesById)
      const ageCapDate = getAgeCapDate(depDose, birth, babyRecords)

      await vaccineStore.updateRecord(depRecord.id, {
        plannedDate: format(newDate, 'yyyy-MM-dd'),
        customPlannedDate: null,
        status: resolvePlannedStatus(newDate, ageCapDate),
        calendarSynced: false,
      })

      doseDates.set(`${depDose.vaccineId}-${depDose.doseIndex}`, newDate)
      doseDatesById.set(depDose.id, newDate)

      // 遞迴：如果後續劑次也有依賴者
      await recalculateSubsequentPlannedDates(depRecord)
    }
  }

  /* =========================
     Export
  ========================= */
  return {
    babies,
    currentBabyId,
    hasOnboarded,
    currentBaby,
    currentBabyRecords,
    allBabiesRecords,
    initFromCloud,
    addBaby,
    switchBaby,
    deleteBaby,
    generateVaccinationRecords,
    markCalendarSynced,
    toggleCalendarSync,
    confirmVaccination,
    unconfirmVaccination,
    markVaccinationAsSkipped,
    restoreManuallySkippedVaccination,
    updateVaccineRecordsToNewVersion,
    updateMaternalRSV,
    updatePlannedDate,
    updateCustomPlannedDate,
    resetAllData,
  }
})
